import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { TrendingUp, ClipboardList, MessageSquare, Bot, FileText, Settings, LogOut, User, ChevronDown, Menu, ChevronLeft, BookOpen, Package, NotebookPen, FlaskConical, X, UserCog, Home } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { toast } from 'sonner';
import NotificationDropdown from './NotificationDropdown';

const Layout = ({ user }) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Close mobile sidebar on route change
  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [location.pathname]);

  // Load user role from database
  useEffect(() => {
    const loadUserRole = async () => {
      if (!user) return;
      
      try {
        const { data, error } = await supabase
          .from('users')
          .select('role, is_active')
          .eq('id', user.id)
          .single();
        
        if (error) {
          console.error('Error fetching user data:', error);
          // don't force logout on simple error
        }
        
        if (data) {
          // Check if user is inactive
          if (data.is_active === false) {
            await supabase.auth.signOut();
            toast.error('Your account has been deactivated. Please contact an administrator.');
            navigate('/login');
            return;
          }
          setUserRole(data.role);
        }
      } catch (error) {
        console.error('Error loading user role:', error);
      }
    };
    
    loadUserRole();
  }, [user, navigate]);

  // Real-time check for user deactivation
  useEffect(() => {
    if (!user) return;

    // Periodic check every 30 seconds
    const checkUserStatus = async () => {
      try {
        const { data, error } = await supabase
          .from('users')
          .select('is_active')
          .eq('id', user.id)
          .single();

        // Only logout if explicitly inactive
        if (data && data.is_active === false) {
          await supabase.auth.signOut();
          toast.error('Your account has been deactivated. Please contact an administrator.');
          navigate('/login');
        }
      } catch (error) {
        console.error('Error checking user status:', error);
      }
    };

    // Check immediately
    checkUserStatus();

    // Then check every 30 seconds
    const intervalId = setInterval(checkUserStatus, 30000);

    // Subscribe to real-time changes for immediate logout
    const channel = supabase
      .channel('user-status-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'users',
          filter: `id=eq.${user.id}`
        },
        async (payload) => {
          console.log('User status changed:', payload);
          
          // Check if user was deactivated
          if (payload.new.is_active === false) {
            // Sign out immediately
            await supabase.auth.signOut();
            toast.error('Your account has been deactivated. Please contact an administrator.');
            navigate('/login');
          }
        }
      )
      .subscribe();

    return () => {
      clearInterval(intervalId);
      supabase.removeChannel(channel);
    };
  }, [user, navigate]);

  // Load sidebar preference from localStorage
  useEffect(() => {
    const savedState = localStorage.getItem('compass_sidebar_collapsed');
    if (savedState !== null) {
      setSidebarCollapsed(JSON.parse(savedState));
    }
  }, []);

  // Save sidebar preference to localStorage
  const toggleSidebar = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem('compass_sidebar_collapsed', JSON.stringify(newState));
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      toast.error('Error logging out');
    }
  };

  // Get user display name or email
  const getUserName = () => {
    if (!user) return 'User';
    return user.email?.split('@')[0] || 'User';
  };

  // Get page title based on current route
  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/overview' || path === '/') return 'Overview';
    if (path === '/tasks') return 'Tasks';
    if (path === '/team-chat') return 'Team Chat';
    if (path === '/logger') return 'Daily Standup';
    if (path === '/personal-notes') return 'Personal Notes';
    if (path.startsWith('/experiment-tracking')) return window.innerWidth < 768 ? 'Projects & Experiments' : 'Project & Experiments Tracking';
    if (path === '/lab-store') return 'Lab Store';
    if (path === '/lab-store/3d-printer') return '3D Printer Usage';
    if (path === '/lab-store/inventory') return 'Lab Inventory';
    if (path === '/inventory') return 'Lab Store';
    if (path === '/ai-assistant') return 'AI Assistant';
    if (path === '/documents') return 'Document Library';
    if (path === '/settings') return 'Settings';
    if (path === '/profile') return 'Profile';
    return 'Dashboard';
  };

  // Some routes (like Team Chat) manage their own inner scrolling
  const isTeamChat = location.pathname === '/team-chat';

  return (
    <div className="flex h-[100dvh] w-screen overflow-hidden bg-[#0a0e1a]">
      {/* Mobile Sidebar Backdrop - Only show on desktop, hidden on mobile */}
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 h-[100dvh] w-screen bg-black/60 backdrop-blur-sm z-40 hidden lg:block"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Desktop Sidebar - Hidden on mobile */}
      <div
        className={`
          hidden lg:flex
          relative z-50 flex-col bg-[#0a0e1a] border-r border-white/8 transition-all duration-300 ease-in-out
          ${sidebarCollapsed ? 'lg:w-[70px]' : 'lg:w-[250px]'}
        `}
        data-testid="sidebar"
        style={{
          borderTopRightRadius: '20px',
          borderBottomRightRadius: '20px'
        }}
      >
        {/* Logo & Toggle */}
        <div className={`${sidebarCollapsed ? 'p-5 pt-5' : 'p-8'} border-b border-white/8 flex items-center justify-between`}>
          {!sidebarCollapsed && (
            <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent" data-testid="logo">
              Compass
            </h1>
          )}

          {/* Desktop Toggle Button */}
          <button
            onClick={toggleSidebar}
            className={`flex ${sidebarCollapsed ? 'w-12 h-12 mx-auto' : 'p-2'} rounded-xl hover:bg-white/10 transition-all duration-200 text-gray-400 hover:text-white items-center justify-center`}
            data-testid="sidebar-toggle"
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {sidebarCollapsed ? <Menu size={24} /> : <ChevronLeft size={20} />}
          </button>
        </div>

        {/* Navigation */}
        <nav className={`flex-1 ${sidebarCollapsed ? 'px-3 py-5' : 'p-4'} space-y-3 ${sidebarCollapsed ? 'overflow-hidden' : 'overflow-y-auto scrollbar-thin'}`} data-testid="nav-menu">
          <NavLink
            to="/overview"
            className={({ isActive }) =>
              sidebarCollapsed
                ? `flex items-center justify-center w-12 h-12 mx-auto rounded-xl transition-all duration-200 relative group cursor-pointer ${isActive ? 'bg-blue-500/15 text-blue-400' : 'text-gray-400 hover:bg-white/8 hover:text-white'}`
                : `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors relative group ${isActive ? 'bg-blue-500/10 text-blue-400 border-l-4 border-blue-500 -ml-4 pl-[calc(1rem-4px)]' : 'text-gray-400 hover:bg-white/5 hover:text-gray-300'}`
            }
            data-testid="nav-overview"
            title={sidebarCollapsed ? "Overview" : ""}
          >
            <TrendingUp size={24} className="shrink-0" />
            {!sidebarCollapsed && <span className="font-medium truncate">Overview</span>}
            {sidebarCollapsed && (
              <div className="absolute left-full ml-3 px-3 py-2 bg-gray-900/90 backdrop-blur-lg text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none whitespace-nowrap z-50 shadow-lg">
                Overview
              </div>
            )}
          </NavLink>

          <NavLink
            to="/tasks"
            className={({ isActive }) =>
              sidebarCollapsed
                ? `flex items-center justify-center w-12 h-12 mx-auto rounded-xl transition-all duration-200 relative group cursor-pointer ${isActive ? 'bg-blue-500/15 text-blue-400' : 'text-gray-400 hover:bg-white/8 hover:text-white'}`
                : `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors relative group ${isActive ? 'bg-blue-500/10 text-blue-400 border-l-4 border-blue-500 -ml-4 pl-[calc(1rem-4px)]' : 'text-gray-400 hover:bg-white/5 hover:text-gray-300'}`
            }
            data-testid="nav-tasks"
            title={sidebarCollapsed ? "Tasks" : ""}
          >
            <ClipboardList size={24} className="shrink-0" />
            {!sidebarCollapsed && <span className="font-medium truncate">Tasks</span>}
            {sidebarCollapsed && (
              <div className="absolute left-full ml-3 px-3 py-2 bg-gray-900/90 backdrop-blur-lg text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none whitespace-nowrap z-50 shadow-lg">
                Tasks
              </div>
            )}
          </NavLink>

          <NavLink
            to="/team-chat"
            className={({ isActive }) =>
              sidebarCollapsed
                ? `flex items-center justify-center w-12 h-12 mx-auto rounded-xl transition-all duration-200 relative group cursor-pointer ${isActive ? 'bg-blue-500/15 text-blue-400' : 'text-gray-400 hover:bg-white/8 hover:text-white'}`
                : `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors relative group ${isActive ? 'bg-blue-500/10 text-blue-400 border-l-4 border-blue-500 -ml-4 pl-[calc(1rem-4px)]' : 'text-gray-400 hover:bg-white/5 hover:text-gray-300'}`
            }
            data-testid="nav-team-chat"
            title={sidebarCollapsed ? "Team Chat" : ""}
          >
            <MessageSquare size={24} className="shrink-0" />
            {!sidebarCollapsed && <span className="font-medium truncate">Team Chat</span>}
            {sidebarCollapsed && (
              <div className="absolute left-full ml-3 px-3 py-2 bg-gray-900/90 backdrop-blur-lg text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none whitespace-nowrap z-50 shadow-lg">
                Team Chat
              </div>
            )}
          </NavLink>

          <NavLink
            to="/logger"
            className={({ isActive }) =>
              sidebarCollapsed
                ? `flex items-center justify-center w-12 h-12 mx-auto rounded-xl transition-all duration-200 relative group cursor-pointer ${isActive ? 'bg-blue-500/15 text-blue-400' : 'text-gray-400 hover:bg-white/8 hover:text-white'}`
                : `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors relative group ${isActive ? 'bg-blue-500/10 text-blue-400 border-l-4 border-blue-500 -ml-4 pl-[calc(1rem-4px)]' : 'text-gray-400 hover:bg-white/5 hover:text-gray-300'}`
            }
            data-testid="nav-logger"
            title={sidebarCollapsed ? "Daily Standup" : ""}
          >
            <BookOpen size={24} className="shrink-0" />
            {!sidebarCollapsed && <span className="font-medium truncate">Daily Standup</span>}
            {sidebarCollapsed && (
              <div className="absolute left-full ml-3 px-3 py-2 bg-gray-900/90 backdrop-blur-lg text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none whitespace-nowrap z-50 shadow-lg">
                Daily Standup
              </div>
            )}
          </NavLink>

          <NavLink
            to="/experiment-tracking"
            className={({ isActive }) =>
              sidebarCollapsed
                ? `flex items-center justify-center w-12 h-12 mx-auto rounded-xl transition-all duration-200 relative group cursor-pointer ${isActive ? 'bg-blue-500/15 text-blue-400' : 'text-gray-400 hover:bg-white/8 hover:text-white'}`
                : `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors relative group ${isActive ? 'bg-blue-500/10 text-blue-400 border-l-4 border-blue-500 -ml-4 pl-[calc(1rem-4px)]' : 'text-gray-400 hover:bg-white/5 hover:text-gray-300'}`
            }
            data-testid="nav-experiment-tracking"
            title={sidebarCollapsed ? "Project & Experiments Tracking" : ""}
          >
            <FlaskConical size={24} className="shrink-0" />
            {!sidebarCollapsed && <span className="font-medium text-sm leading-tight break-words">Project & Experiments</span>}
            {sidebarCollapsed && (
              <div className="absolute left-full ml-3 px-3 py-2 bg-gray-900/90 backdrop-blur-lg text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none whitespace-nowrap z-50 shadow-lg">
                Project & Experiments Tracking
              </div>
            )}
          </NavLink>

          <NavLink
            to="/lab-store"
            className={({ isActive }) =>
              sidebarCollapsed
                ? `flex items-center justify-center w-12 h-12 mx-auto rounded-xl transition-all duration-200 relative group cursor-pointer ${isActive ? 'bg-blue-500/15 text-blue-400' : 'text-gray-400 hover:bg-white/8 hover:text-white'}`
                : `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors relative group ${isActive ? 'bg-blue-500/10 text-blue-400 border-l-4 border-blue-500 -ml-4 pl-[calc(1rem-4px)]' : 'text-gray-400 hover:bg-white/5 hover:text-gray-300'}`
            }
            data-testid="nav-lab-store"
            title={sidebarCollapsed ? "Lab Store" : ""}
          >
            <Package size={24} className="shrink-0" />
            {!sidebarCollapsed && <span className="font-medium truncate">Lab Store</span>}
            {sidebarCollapsed && (
              <div className="absolute left-full ml-3 px-3 py-2 bg-gray-900/90 backdrop-blur-lg text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none whitespace-nowrap z-50 shadow-lg">
                Lab Store
              </div>
            )}
          </NavLink>

          <NavLink
            to="/documents"
            className={({ isActive }) =>
              sidebarCollapsed
                ? `flex items-center justify-center w-12 h-12 mx-auto rounded-xl transition-all duration-200 relative group cursor-pointer ${isActive ? 'bg-blue-500/15 text-blue-400' : 'text-gray-400 hover:bg-white/8 hover:text-white'}`
                : `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors relative group ${isActive ? 'bg-blue-500/10 text-blue-400 border-l-4 border-blue-500 -ml-4 pl-[calc(1rem-4px)]' : 'text-gray-400 hover:bg-white/5 hover:text-gray-300'}`
            }
            data-testid="nav-documents"
            title={sidebarCollapsed ? "Documents" : ""}
          >
            <FileText size={24} className="shrink-0" />
            {!sidebarCollapsed && <span className="font-medium truncate">Documents</span>}
            {sidebarCollapsed && (
              <div className="absolute left-full ml-3 px-3 py-2 bg-gray-900/90 backdrop-blur-lg text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none whitespace-nowrap z-50 shadow-lg">
                Documents
              </div>
            )}
          </NavLink>
        </nav>

        {/* User Section */}
        <div className={`${sidebarCollapsed ? 'p-3 pb-5' : 'p-4'} border-t border-white/10`}>
          {sidebarCollapsed ? (
            <div className="flex flex-col items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center relative group hover:scale-105 transition-all duration-200 cursor-pointer">
                <User size={20} />
                <div className="absolute left-full ml-3 px-3 py-2 bg-gray-900/90 backdrop-blur-lg text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none whitespace-nowrap z-50 shadow-lg">
                  {getUserName()}
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="w-10 h-10 rounded-xl text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-all duration-200 flex items-center justify-center relative group"
                data-testid="logout-btn"
                title="Logout"
              >
                <LogOut size={20} />
                <div className="absolute left-full ml-3 px-3 py-2 bg-gray-900/90 backdrop-blur-lg text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none whitespace-nowrap z-50 shadow-lg">
                  Logout
                </div>
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-white/5 cursor-pointer hover:bg-white/10 transition-colors" data-testid="user-section">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                <User size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{getUserName()}</div>
                <div className="text-xs text-gray-400 truncate">{user?.email}</div>
              </div>
              <button
                onClick={handleLogout}
                className="text-gray-400 hover:text-red-400 transition-colors"
                data-testid="logout-btn"
              >
                <LogOut size={18} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col h-[100dvh] overflow-hidden w-full max-w-full relative">
        {/* Top Bar */}
        <div className="h-12 lg:h-16 bg-[#1a1f2e] border-b border-white/10 flex items-center justify-between px-2 lg:px-8" data-testid="top-bar">
          <div className="flex items-center gap-2 lg:gap-4 flex-1 min-w-0 pr-1 lg:pr-4">
            {/* No mobile sidebar toggle - using bottom nav instead */}
            <h2 className="text-base lg:text-xl font-semibold truncate" data-testid="page-title">{getPageTitle()}</h2>
          </div>

          <div className="flex items-center gap-1.5 lg:gap-2 flex-shrink-0">
            {/* Mobile & Desktop: Show notifications and AI */}
            <div className="flex items-center">
              <NotificationDropdown />
            </div>
            <NavLink
              to="/ai-assistant"
              className={({ isActive }) =>
                `flex flex-col lg:flex-row items-center justify-center gap-0.5 lg:gap-2 px-2 py-1 lg:p-2.5 rounded-xl transition-all duration-200 ${isActive
                  ? 'bg-purple-500/15 text-purple-400 shadow-[0_0_15px_rgba(168,85,247,0.1)]'
                  : 'text-gray-400 hover:bg-white/8 hover:text-white'
                }`
              }
              title="AI Assistant"
            >
              <Bot size={18} className="lg:w-5 lg:h-5" />
              <span className="text-[9px] lg:hidden font-medium">AI</span>
            </NavLink>
            
            {/* Mobile Profile Icon */}
            <NavLink
              to="/profile"
              className={({ isActive }) =>
                `flex flex-col lg:hidden items-center justify-center gap-0.5 px-2 py-1 rounded-xl transition-all duration-200 ${isActive
                  ? 'bg-blue-500/15 text-blue-400'
                  : 'text-gray-400 hover:bg-white/8 hover:text-white'
                }`
              }
              title="Profile"
            >
              <div className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                <User size={12} />
              </div>
              <span className="text-[9px] font-medium">Profile</span>
            </NavLink>

            {/* Desktop only: Personal Notes */}
            <div className="hidden lg:flex items-center gap-2">
              <NavLink
                to="/personal-notes"
                className={({ isActive }) =>
                  `p-2.5 rounded-xl transition-all duration-200 ${isActive
                    ? 'bg-blue-500/15 text-blue-400 shadow-[0_0_15px_rgba(59,130,246,0.1)]'
                    : 'text-gray-400 hover:bg-white/8 hover:text-white'
                  }`
                }
                title="Personal Notes"
              >
                <NotebookPen size={20} />
              </NavLink>
              <div className="w-px h-6 bg-white/10 mx-2" />
            </div>

            {/* Desktop User Menu */}
            <div className="relative hidden lg:block">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-2 px-2 lg:px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                data-testid="user-dropdown-btn"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                  <User size={16} />
                </div>
                <span className="text-sm font-medium hidden sm:block">{getUserName()}</span>
                <ChevronDown size={16} className="text-gray-400" />
              </button>
              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-48 glass-card p-2 z-50" data-testid="user-dropdown-menu">
                  {userRole === 'admin' && (
                    <>
                      <button
                        onClick={() => {
                          navigate('/admin/users');
                          setShowUserMenu(false);
                        }}
                        className="w-full text-left px-4 py-2 rounded-lg hover:bg-white/5 transition-colors flex items-center gap-2 text-gray-300 mb-1"
                        data-testid="user-dropdown-admin"
                      >
                        <UserCog size={16} />
                        User Management
                      </button>
                      <div className="h-px bg-white/10 my-1" />
                    </>
                  )}
                  <button
                    onClick={() => {
                      navigate('/settings');
                      setShowUserMenu(false);
                    }}
                    className="w-full text-left px-4 py-2 rounded-lg hover:bg-white/5 transition-colors flex items-center gap-2 text-gray-300"
                    data-testid="user-dropdown-settings"
                  >
                    <Settings size={16} />
                    Settings
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Page Content */}
        <div className={`flex-1 w-full max-w-full ${isTeamChat ? 'overflow-hidden' : 'overflow-y-auto scrollbar-thin'} pb-20 lg:pb-0`} data-testid="page-content">
          <Outlet />
        </div>

        {/* Mobile Bottom Navigation */}
        <div className="fixed bottom-0 left-0 right-0 bg-[#1a1f2e]/98 backdrop-blur-xl border-t border-white/10 lg:hidden z-50 safe-bottom">
          <div className="grid grid-cols-5 gap-1 px-2 py-2">
            {/* Home/Overview */}
            <NavLink
              to="/overview"
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 py-2 px-1 rounded-xl transition-all duration-200 ${
                  isActive
                    ? 'bg-blue-500/15 text-blue-400'
                    : 'text-gray-400 active:bg-white/5'
                }`
              }
            >
              <Home size={22} className="shrink-0" />
              <span className="text-[10px] font-medium">Home</span>
            </NavLink>

            {/* Team Chat */}
            <NavLink
              to="/team-chat"
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 py-2 px-1 rounded-xl transition-all duration-200 ${
                  isActive
                    ? 'bg-blue-500/15 text-blue-400'
                    : 'text-gray-400 active:bg-white/5'
                }`
              }
            >
              <MessageSquare size={22} className="shrink-0" />
              <span className="text-[10px] font-medium">Team Chat</span>
            </NavLink>

            {/* Tasks */}
            <NavLink
              to="/tasks"
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 py-2 px-1 rounded-xl transition-all duration-200 ${
                  isActive
                    ? 'bg-blue-500/15 text-blue-400'
                    : 'text-gray-400 active:bg-white/5'
                }`
              }
            >
              <ClipboardList size={22} className="shrink-0" />
              <span className="text-[10px] font-medium">Tasks</span>
            </NavLink>

            {/* Experiments */}
            <NavLink
              to="/experiment-tracking"
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 py-2 px-1 rounded-xl transition-all duration-200 ${
                  isActive
                    ? 'bg-blue-500/15 text-blue-400'
                    : 'text-gray-400 active:bg-white/5'
                }`
              }
            >
              <FlaskConical size={22} className="shrink-0" />
              <span className="text-[10px] font-medium">Project</span>
            </NavLink>

            {/* Lab Store */}
            <NavLink
              to="/lab-store"
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 py-2 px-1 rounded-xl transition-all duration-200 ${
                  isActive
                    ? 'bg-blue-500/15 text-blue-400'
                    : 'text-gray-400 active:bg-white/5'
                }`
              }
            >
              <Package size={22} className="shrink-0" />
              <span className="text-[10px] font-medium">Lab Store</span>
            </NavLink>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Layout;
